/**
 * FigmaExportModal — Export current page as SVG for Figma paste (v2).
 *
 * Generates a clean SVG with native <text> elements so that
 * when pasted into Figma, all text remains live and editable.
 *
 * Features:
 *   - Viewport / Full page capture
 *   - Include/exclude images toggle
 *   - Copy SVG to clipboard (paste in Figma with Ctrl+V)
 *   - Download .svg file (drag into Figma)
 *   - SVG preview panel
 *   - Status feedback
 */

import { useState, useRef, useCallback } from "react";
import {
  Figma,
  X,
  Copy,
  Download,
  Loader2,
  Monitor,
  Maximize2,
  Image as ImageIcon,
  ImageOff,
  CheckCircle2,
  AlertTriangle,
  Info,
  Type,
  Eye,
} from "lucide-react";
import {
  generateFigmaSvg,
  copySvgToClipboard,
  downloadSvgFile,
  type FigmaExportOptions,
} from "../../utils/figmaExport";
import { useTemplateType, useRelativePath } from "./TemplateContext";
import { navigation as brandingNav, type NavItem } from "../../siteConfig";
import { navigation as storeNav } from "../../storeSiteConfig";

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

type CaptureMode = "viewport" | "fullpage";

function flattenNav(items: NavItem[]): { label: string; path: string }[] {
  const flat: { label: string; path: string }[] = [];
  for (const item of items) {
    flat.push({ label: item.label, path: item.path });
    if (item.children) {
      for (const child of item.children) {
        flat.push({ label: child.label, path: child.path });
      }
    }
  }
  return flat;
}

function getCurrentPageName(relativePath: string, templateType: string): string {
  const nav = templateType === "store" ? storeNav : brandingNav;
  const flat = flattenNav(nav);
  const match = flat.find((item) => item.path === relativePath);
  return match?.label || "\u05E2\u05DE\u05D5\u05D3";
}

export function FigmaExportModal({ onClose }: { onClose: () => void }) {
  const templateType = useTemplateType();
  const relativePath = useRelativePath();
  const [mode, setMode] = useState<CaptureMode>("viewport");
  const [includeImages, setIncludeImages] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState<"idle" | "copied" | "downloaded" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [svgStats, setSvgStats] = useState<{ size: string; textCount: number } | null>(null);
  const [previewSvg, setPreviewSvg] = useState<string | null>(null);
  const svgCacheRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const pageName = getCurrentPageName(relativePath, templateType);

  const doGenerate = useCallback(async (): Promise<string | null> => {
    setIsExporting(true);
    setStatus("idle");
    setErrorMsg("");
    setSvgStats(null);
    svgCacheRef.current = null;

    // Hide modal during capture
    const modalEl = document.querySelector("[data-figma-export-modal]") as HTMLElement | null;
    if (modalEl) modalEl.style.setProperty("visibility", "hidden", "important");

    await new Promise((r) => setTimeout(r, 200));

    try {
      const opts: FigmaExportOptions = {
        mode,
        includeImages,
      };

      const { svg } = await generateFigmaSvg(opts);
      svgCacheRef.current = svg;

      // Stats
      const sizeKB = (new Blob([svg]).size / 1024).toFixed(1);
      const textCount = (svg.match(/<text[\s>]/g) || []).length;
      setSvgStats({ size: sizeKB + " KB", textCount });

      return svg;
    } catch (err) {
      console.error("[FigmaExport] Generation error:", err);
      setErrorMsg(err instanceof Error ? err.message : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA SVG");
      setStatus("error");
      return null;
    } finally {
      if (modalEl) modalEl.style.removeProperty("visibility");
      setIsExporting(false);
    }
  }, [mode, includeImages]);

  const handleCopy = useCallback(async () => {
    const svg = await doGenerate();
    if (!svg) return;

    const success = copySvgToClipboard(svg);
    if (success) {
      setStatus("copied");
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setStatus("idle"), 5000);
    } else {
      setStatus("error");
      setErrorMsg("\u05D4\u05D4\u05E2\u05EA\u05E7\u05D4 \u05DC\u05DC\u05D5\u05D7 \u05E0\u05DB\u05E9\u05DC\u05D4");
    }
  }, [doGenerate]);

  const handleDownload = useCallback(async () => {
    const svg = await doGenerate();
    if (!svg) return;

    const safePageName = pageName.replace(/[^a-zA-Z0-9\u0590-\u05FF\u200F\u200E]/g, "-");
    const ts = new Date().toISOString().slice(0, 10);
    downloadSvgFile(svg, `${safePageName}-figma-${ts}.svg`);
    setStatus("downloaded");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setStatus("idle"), 5000);
  }, [doGenerate, pageName]);

  const handlePreview = useCallback(async () => {
    const svg = await doGenerate();
    if (svg) setPreviewSvg(svg);
  }, [doGenerate]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-figma-export-modal
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col ${
          previewSvg ? "w-full max-w-5xl max-h-[90vh]" : "w-full max-w-md"
        }`}
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Figma className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-[#1a1a2e] text-base leading-tight" style={{ fontWeight: 700 }}>
                {"\u05D9\u05D9\u05E6\u05D5\u05D0 \u05DC\u05E4\u05D9\u05D2\u05DE\u05D4"}
              </h2>
              <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                {"\u05E2\u05DE\u05D5\u05D3 \u05E0\u05D5\u05DB\u05D7\u05D9: "}{pageName}
              </p>
            </div>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClose(); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </span>
        </div>

        {/* Body — flex row when preview is visible */}
        <div className={`flex ${previewSvg ? "flex-row" : "flex-col"} overflow-hidden flex-1 min-h-0`}>
          {/* Settings panel */}
          <div className={`p-5 space-y-4 overflow-y-auto ${previewSvg ? "w-[340px] shrink-0 border-l border-gray-100" : ""}`}>
            {/* Capture Mode */}
            <div>
              <label className="text-xs text-gray-500 mb-2 block" style={{ fontWeight: 500 }}>
                {"\u05D0\u05D6\u05D5\u05E8 \u05DC\u05DB\u05D9\u05D3\u05D4"}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <ModeCard
                  active={mode === "viewport"}
                  onClick={() => { setMode("viewport"); setPreviewSvg(null); }}
                  icon={<Monitor className="w-4 h-4" />}
                  label={"\u05D0\u05D6\u05D5\u05E8 \u05E0\u05E8\u05D0\u05D4"}
                />
                <ModeCard
                  active={mode === "fullpage"}
                  onClick={() => { setMode("fullpage"); setPreviewSvg(null); }}
                  icon={<Maximize2 className="w-4 h-4" />}
                  label={"\u05E2\u05DE\u05D5\u05D3 \u05DE\u05DC\u05D0"}
                />
              </div>
            </div>

            {/* Images toggle */}
            <div>
              <label className="text-xs text-gray-500 mb-2 block" style={{ fontWeight: 500 }}>
                {"\u05EA\u05DE\u05D5\u05E0\u05D5\u05EA"}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <ModeCard
                  active={!includeImages}
                  onClick={() => { setIncludeImages(false); setPreviewSvg(null); }}
                  icon={<ImageOff className="w-4 h-4" />}
                  label={"\u05DC\u05DC\u05D0 \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA"}
                />
                <ModeCard
                  active={includeImages}
                  onClick={() => { setIncludeImages(true); setPreviewSvg(null); }}
                  icon={<ImageIcon className="w-4 h-4" />}
                  label={"\u05DB\u05D5\u05DC\u05DC \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA"}
                />
              </div>
            </div>

            {/* Info box */}
            <div className="flex items-start gap-2 p-3 bg-purple-50 rounded-xl border border-purple-100">
              <Type className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-purple-700 leading-relaxed">
                {"\u05D4\u05D8\u05E7\u05E1\u05D8 \u05D9\u05D9\u05E9\u05D0\u05E8 \u05D7\u05D9 \u05D5\u05E0\u05D9\u05EA\u05DF \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4 \u05D1\u05E4\u05D9\u05D2\u05DE\u05D4. \u05D4\u05E2\u05EA\u05D9\u05E7\u05D5 \u05D0\u05EA \u05D4-SVG \u05D5\u05D4\u05D3\u05D1\u05D9\u05E7\u05D5 \u05D9\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05E4\u05D9\u05D2\u05DE\u05D4 \u05E2\u05DD Ctrl+V."}
              </p>
            </div>

            {/* Instructions */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <Info className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div className="text-[11px] text-gray-500 leading-relaxed space-y-1">
                <p style={{ fontWeight: 600 }}>{"\u05D0\u05D9\u05DA \u05DC\u05D4\u05E9\u05EA\u05DE\u05E9:"}</p>
                <ol className="list-decimal list-inside space-y-0.5 mr-1">
                  <li>{"\u05DC\u05D7\u05E6\u05D5 \"\u05D4\u05E2\u05EA\u05E7 SVG\" \u05D0\u05D5 \"\u05D4\u05D5\u05E8\u05D3 \u05E7\u05D5\u05D1\u05E5\""}</li>
                  <li>{"\u05E2\u05D1\u05E8\u05D5 \u05DC\u05E4\u05D9\u05D2\u05DE\u05D4"}</li>
                  <li>{"\u05D4\u05D3\u05D1\u05D9\u05E7\u05D5 \u05E2\u05DD Ctrl+V \u05D0\u05D5 \u05D2\u05E8\u05E8\u05D5 \u05D0\u05EA \u05D4\u05E7\u05D5\u05D1\u05E5"}</li>
                  <li>{"\u05D4\u05D8\u05E7\u05E1\u05D8 \u05D9\u05D4\u05D9\u05D4 \u05E0\u05D9\u05EA\u05DF \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4!"}</li>
                </ol>
              </div>
            </div>

            {/* Stats */}
            {svgStats && (
              <div className="flex items-center gap-3 text-[10px] text-gray-400">
                <span>{"\u05D2\u05D5\u05D3\u05DC: "}{svgStats.size}</span>
                <span className="text-gray-300">{"\u00B7"}</span>
                <span>{svgStats.textCount}{" \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9 \u05D8\u05E7\u05E1\u05D8"}</span>
              </div>
            )}

            {/* Status */}
            {status === "copied" && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl border border-green-200 text-green-700 text-xs">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span style={{ fontWeight: 500 }}>{"\u05D4-SVG \u05D4\u05D5\u05E2\u05EA\u05E7! \u05D4\u05D3\u05D1\u05D9\u05E7\u05D5 \u05D1\u05E4\u05D9\u05D2\u05DE\u05D4 \u05E2\u05DD Ctrl+V"}</span>
              </div>
            )}
            {status === "downloaded" && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl border border-green-200 text-green-700 text-xs">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span style={{ fontWeight: 500 }}>{"\u05D4\u05E7\u05D5\u05D1\u05E5 \u05D4\u05D5\u05E8\u05D3! \u05D2\u05E8\u05E8\u05D5 \u05D0\u05D5\u05EA\u05D5 \u05DC\u05E4\u05D9\u05D2\u05DE\u05D4"}</span>
              </div>
            )}
            {status === "error" && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-200 text-red-700 text-xs">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span style={{ fontWeight: 500 }}>{errorMsg || "\u05E9\u05D2\u05D9\u05D0\u05D4"}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2">
              {/* Copy */}
              <span
                role="button"
                tabIndex={0}
                onClick={isExporting ? undefined : handleCopy}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isExporting) handleCopy(); }}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs cursor-pointer shadow-md ${
                  isExporting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-l from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-purple-500/20"
                }`}
                style={{ fontWeight: 600 }}
              >
                {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                {isExporting ? "\u05DE\u05D9\u05D9\u05E6\u05E8..." : "\u05D4\u05E2\u05EA\u05E7 SVG"}
              </span>

              {/* Download */}
              <span
                role="button"
                tabIndex={0}
                onClick={isExporting ? undefined : handleDownload}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isExporting) handleDownload(); }}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs cursor-pointer border-2 ${
                  isExporting
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                }`}
                style={{ fontWeight: 600 }}
              >
                {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {isExporting ? "\u05DE\u05D9\u05D9\u05E6\u05E8..." : "\u05D4\u05D5\u05E8\u05D3 \u05E7\u05D5\u05D1\u05E5"}
              </span>
            </div>

            {/* Preview button */}
            <span
              role="button"
              tabIndex={0}
              onClick={isExporting ? undefined : handlePreview}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isExporting) handlePreview(); }}
              className={`flex items-center justify-center gap-2 w-full px-3 py-2 rounded-xl text-xs cursor-pointer border border-gray-200 ${
                isExporting
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
              style={{ fontWeight: 500 }}
            >
              <Eye className="w-3.5 h-3.5" />
              {"\u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4"}
            </span>
          </div>

          {/* Preview panel */}
          {previewSvg && (
            <div className="flex-1 min-w-0 bg-gray-50 border-gray-200 overflow-auto p-4" dir="ltr">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden inline-block min-w-full">
                <div
                  dangerouslySetInnerHTML={{ __html: previewSvg }}
                  style={{ maxWidth: "100%", overflow: "auto" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Mode Card ── */

function ModeCard({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer ${
        active
          ? "border-purple-500 bg-purple-50/50 shadow-sm"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <span className={active ? "text-purple-600" : "text-gray-400"}>{icon}</span>
      <span
        className={`text-xs ${active ? "text-purple-700" : "text-gray-600"}`}
        style={{ fontWeight: 600 }}
      >
        {label}
      </span>
    </span>
  );
}

/* ── Trigger button for AdminBar ── */

export function FigmaExportButton() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setShowModal(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowModal(true); }}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/10 cursor-pointer text-pink-200/60 hover:text-white"
        title={"\u05D9\u05D9\u05E6\u05D5\u05D0 \u05DC\u05E4\u05D9\u05D2\u05DE\u05D4"}
      >
        <Figma className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-[11px]" style={{ fontWeight: 500 }}>{"\u05DC\u05E4\u05D9\u05D2\u05DE\u05D4"}</span>
      </span>
      {showModal && <FigmaExportModal onClose={() => setShowModal(false)} />}
    </>
  );
}